import { Injectable } from '@angular/core';
import { QuestionDefinitionModel } from '../models/form/question-definition-model';
import { ApplicationDefinitionService } from './application-definition.service';
import { SurveyDefinitionService } from './survey-definition.service';
import { ConditionDefinitionModel } from 'src/app/admin/admin-home/forms/shared-components/question-logic-definition/condition-definition.models';
import { AttributeDefinitionCondition } from '../models/attributes/attributeDefinitionCondition';
import { SnackBarService } from './snackbar.service';
import { EvaluationDefinitionService } from './evaluation-definition.service';

@Injectable({
  providedIn: 'root',
})
export class QuestionLogicService {
  private _questions: QuestionDefinitionModel[] = [];

  private _initialized = false;

  private _currentChildQuestin: QuestionDefinitionModel;
  constructor(
    private appDefService: ApplicationDefinitionService,
    private surveyDefService: SurveyDefinitionService,
    private evaluationDefService: EvaluationDefinitionService,
    private snackBar: SnackBarService
  ) {}

  public initialize() {
    if (this._initialized) return;
    this.appDefService.applicationDefinition.subscribe((appDef) => {
      if (!appDef) return;
      this._questions = appDef.questions.map((question) => QuestionDefinitionModel.parse(question));
    });

    this.surveyDefService.surveyDefinition.subscribe((surveyDef) => {
      if (!surveyDef) return;
      this._questions = surveyDef.questions.map((question) =>
        QuestionDefinitionModel.parse(question)
      );
    });
    this.evaluationDefService.evaluationDefinition.subscribe((evaluationDef) => {
      if (!evaluationDef) return;
      this._questions = evaluationDef.questions.map((question) =>
        QuestionDefinitionModel.parse(question)
      );
    });
    this._initialized = true;
  }

  public get questions(): QuestionDefinitionModel[] {
    return this._questions;
  }

  public getPossibleTargetQuestions(question: QuestionDefinitionModel): ConditionDefinitionModel[] {
    this._currentChildQuestin = question;
    const currentQuestionIndex = this._questions.findIndex(
      (q) => q.attributeDefinition.id == question.attributeDefinition.id
    );
    const index = currentQuestionIndex != -1 ? currentQuestionIndex : this._questions.length;
    const possibleTargetQuestions = this._questions
      .slice(0, index)
      .filter((question) => question.attributeDefinition.isEnumerated);
    return possibleTargetQuestions.map((q) => ConditionDefinitionModel.parse(q));
  }

  public getCurrentTargetQuestion(): ConditionDefinitionModel {
    if (!this._currentChildQuestin.hasCondition) return null;
    const currentTargetQuestion = this._questions.find(
      (q) =>
        q.attributeDefinition.id ==
        this._currentChildQuestin.firstCondition.targetAttributeDefinitionId
    );
    if (!currentTargetQuestion) return null;
    return ConditionDefinitionModel.parse(
      currentTargetQuestion,
      this._currentChildQuestin.firstCondition
    );
  }

  public findQuestion(id: string) {
    this._currentChildQuestin = this._questions.find((q) => q.attributeDefinition.id == id);
    if (!this._currentChildQuestin) return null;
    return ConditionDefinitionModel.parse(
      this._currentChildQuestin,
      this._currentChildQuestin.firstCondition
    );
  }

  public generateCondition(
    conditionDefinitionModel: ConditionDefinitionModel
  ): AttributeDefinitionCondition {
    if (!conditionDefinitionModel) return null;
    const conditionValues = conditionDefinitionModel.conditionValues
      .filter((c) => c.selected)
      .map((x) => x.id);
    if (conditionValues.length === 0) return null;
    let attributeDefinitionCondition: AttributeDefinitionCondition = {
      targetAttributeDefinitionId: conditionDefinitionModel.id,
      conditionValues: conditionValues,
    };
    return attributeDefinitionCondition;
  }

  public getTargetQuestionNames(question: QuestionDefinitionModel) {
    return question.conditions.map(
      (c) =>
        this._questions.find((q) => q.attributeDefinition.id == c.targetAttributeDefinitionId)
          ?.attributeDefinition.name
    );
  }

  public isTargetQuestion(question: QuestionDefinitionModel): boolean {
    const result = this._questions.filter(
      (x) =>
        x.conditions.filter(
          (c) =>
            c.targetAttributeDefinitionId == question.attributeDefinition.id &&
            c.conditionValues.length > 0
        ).length > 0
    ).length;
    return result !== 0;
  }

  public canMove(oldIndex: number, newIndex: number): boolean {
    var question = this._questions[oldIndex];
    let result = true;
    if (this.isTargetQuestion(question)) {
      var childQuestionIndexes = this._questions
        .filter(
          (x) =>
            x.conditions.filter(
              (c) => c.targetAttributeDefinitionId == question.attributeDefinition.id
            ).length > 0
        )
        .map((c) =>
          this._questions.findIndex((q) => q.attributeDefinition.id == c.attributeDefinition.id)
        );
      if (childQuestionIndexes.find((x) => x <= newIndex) != undefined) {
        this.snackBar.open(
          'This question is referenced by another with display logic. It must appear before the referenced question.',
          false
        );
        result = false;
      }
    }

    if (question.hasCondition) {
      var targetQuestionIndexes = question.conditions.map((c) =>
        this._questions.findIndex((q) => q.attributeDefinition.id == c.targetAttributeDefinitionId)
      );
      if (targetQuestionIndexes.find((x) => x >= newIndex) != undefined) {
        this.snackBar.open(
          'This question contains logic that references another question. It must appear after the referenced question.',
          false
        );
        result = false;
      }
    }
    return result;
  }
}
